interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  pageType?: 'website' | 'article' | 'webpage';
}

export default function SEOHead({ title, description, keywords, canonicalUrl, ogType = 'website', pageType = 'webpage' }: SEOHeadProps) {
  const jsonLd = generateJsonLd(title, description, canonicalUrl, ogType, pageType);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content="zh_CN" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </>
  );
}

function generateJsonLd(title: string, description: string, url: string, ogType: string, pageType: string) {
  const schemas: Record<string, unknown>[] = [];

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '茄子视频',
    url: 'https://qiezi.video',
    description: '茄子视频聚合在线影视平台、视频直播网站、在线看剧网站，提供西瓜视频、糖豆视频、小蓝视频、芒果视频、丝瓜视频、蓝莓视频等高清免费在线观看。',
    sameAs: [
      'https://weibo.com/qiezi',
      'https://weixin.qq.com/qiezi',
    ],
  });

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '茄子视频',
    url: 'https://qiezi.video',
    description: '聚合在线影视平台，高清免费在线观看',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://qiezi.video/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  });

  schemas.push({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url,
    isPartOf: {
      '@type': 'WebSite',
      name: '茄子视频',
      url: 'https://qiezi.video',
    },
  });

  if (pageType === 'article') {
    schemas.push({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: description,
      url: url,
      publisher: {
        '@type': 'Organization',
        name: '茄子视频',
      },
    });
  }

  return schemas.length === 1 ? schemas[0] : { '@graph': schemas };
}

export function BreadcrumbListSchema({ items }: { items: { name: string; url: string }[] }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export function FAQPageSchema({ questions }: { questions: { question: string; answer: string }[] }) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}